import { supabase } from "../integrations/supabase/client";

// Response types for token operations
export interface TokenInfoResponse {
  avail: number;
  alloc: number;
  used: number;
  alloc_start: Date | null;
  alloc_end: Date | null;
}

export interface TokenAllowanceResponse {
  allowed: boolean;
  reason: string | null;
  avail: number;
  futAvail: number;
  alloc: number;
  used: number;
  alloc_start: Date | null;
  alloc_end: Date | null;
}

export interface TokenUpdateResponse {
  used: number;
  avail: number;
  alloc: number;
  alloc_start: Date | null;
  alloc_end: Date | null;
}

export class UserTokensService {
  private static instance: UserTokensService;
  private tokenInfo: TokenInfoResponse = {} as TokenInfoResponse;
  private subscribers: ((tokenInfo: TokenInfoResponse) => void)[] = [];

  private constructor() {
    // Initialize by fetching token info
    this.getTokenInfo();
  }

  public static getInstance(): UserTokensService {
    if (!UserTokensService.instance) {
      UserTokensService.instance = new UserTokensService();
    }
    return UserTokensService.instance;
  }

  // Get current token information for the user
  public async getTokenInfo(): Promise<TokenInfoResponse | null> {
    try {
      // First check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      
      // If no authenticated user, return null early
      if (!user) {
        console.log("No authenticated user, skipping token info fetch");
        return null;
      }

      const { data, error } = await supabase
        .functions.invoke("tokens", { method: "GET" });

      if (error) {
        console.error("Error fetching token info:", error);
        return null;
      }

      this.tokenInfo = data;
      this.notifySubscribers();

      return data || null;
    } catch (error) {
      console.error("Error fetching token info:", error);
      return null;
    }
  }

  // Check if usage of tokens would be allowed
  public async checkTokenAllowance(use: number): Promise<TokenAllowanceResponse | null> {
    try {
      const { data, error } = await supabase
        .functions.invoke(`tokens?use=${use}`, { 
          method: "GET"
        });

      if (error) {
        console.error("Error checking token allowance:", error);
        return null;
      }

      return data || null;
    } catch (error) {
      console.error("Error checking token allowance:", error);
      return null;
    }
  }

  // Use tokens (consume tokens)
  public async useTokens(use: number): Promise<TokenUpdateResponse | null> {
    try {
      const { data, error } = await supabase
        .functions.invoke("tokens", {
          method: "PUT",
          body: { use }
        });

      if (error) {
        console.error("Error using tokens:", error);
        return null;
      }

      // Update local state and notify subscribers
      await this.getTokenInfo();

      return data || null;
    } catch (error) {
      console.error("Error using tokens:", error);
      return null;
    }
  }

  // For compatibility with existing pattern
  public async refreshTokenInfo(): Promise<void> {
    await this.getTokenInfo();
  }

  // Subscribe to changes
  public subscribe(
    callback: (tokenInfo: TokenInfoResponse) => void,
  ): () => void {
    this.subscribers.push(callback);
    callback(this.tokenInfo);

    return () => {
      this.subscribers = this.subscribers.filter((cb) => cb !== callback);
    };
  }

  // Notify all subscribers of changes
  private notifySubscribers(): void {
    for (const callback of this.subscribers) {
      callback(this.tokenInfo);
    }
  }
}

export const userTokensService = UserTokensService.getInstance(); 